import React, { useState } from 'react';
import Button from '../UI/Button';
import Input from '../UI/Input';
import { useAppSelector } from '../../hooks/redux';
import { api } from '../../services/api';

interface ProjectFile {
  id?: string;
  _id?: string;
  type: 'image' | 'video' | 'document';
  filename: string;
  originalFilename: string;
  url: string;
  thumbnailUrl?: string;
  mimeType: string;
  size: number;
  description?: string;
  synopsis?: string;
  uploadedBy?: string;
  uploadedAt?: string;
  updatedAt?: string;
  updatedBy?: string;
  currentVersion?: number;
  versions?: Array<{
    version: number;
    url: string;
    uploadedAt: string;
    uploadedBy: string;
    description?: string;
    synopsis?: string;
    changeNotes?: string;
  }>;
}

interface ProjectFilesManagerProps {
  projectId: string;
  files: ProjectFile[];
  onFilesChange: (files: ProjectFile[]) => void;
}

const ProjectFilesManager: React.FC<ProjectFilesManagerProps> = ({
  projectId,
  files,
  onFilesChange,
}) => {
  const { accessToken } = useAppSelector((state) => state.auth);
  const [editingFileId, setEditingFileId] = useState<string | null>(null);
  const [fileMetadata, setFileMetadata] = useState<Record<string, { description: string; synopsis: string }>>({});
  const [updatingFileId, setUpdatingFileId] = useState<string | null>(null);
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const [newVersionFile, setNewVersionFile] = useState<{ fileId: string; file: File | null; changeNotes: string } | null>(null);

  const handleEdit = (file: ProjectFile) => {
    const fileId = file.id || file._id || '';
    setEditingFileId(fileId);
    setFileMetadata({
      ...fileMetadata,
      [fileId]: {
        description: file.description || '',
        synopsis: file.synopsis || '',
      },
    });
  };

  const handleSaveMetadata = async (file: ProjectFile) => {
    const fileId = file.id || file._id || '';
    const metadata = fileMetadata[fileId];
    if (!metadata || !accessToken) return;

    try {
      const updatedFile = await api.projectFiles.updateFile(
        projectId,
        fileId,
        {
          description: metadata.description,
          synopsis: metadata.synopsis,
        },
        undefined, // No new file version
        accessToken
      );

      const updatedFiles = files.map((f) => {
        const fId = f.id || f._id || '';
        if (fId === fileId) {
          return updatedFile;
        }
        return f;
      });

      onFilesChange(updatedFiles);
      setEditingFileId(null);
    } catch (error) {
      console.error('Error updating file metadata:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingFileId(null);
  };

  const handleUpdateFile = async (file: ProjectFile) => {
    const fileId = file.id || file._id || '';
    if (!newVersionFile || newVersionFile.fileId !== fileId || !newVersionFile.file) return;

    setUpdatingFileId(fileId);
    try {
      const formData = new FormData();
      formData.append('file', newVersionFile.file);
      const metadata = fileMetadata[fileId] || { description: '', synopsis: '' };
      formData.append('description', metadata.description);
      formData.append('synopsis', metadata.synopsis);
      formData.append('changeNotes', newVersionFile.changeNotes);

      const updatedFile = await api.projectFiles.updateFile(
        projectId,
        fileId,
        {
          description: metadata.description,
          synopsis: metadata.synopsis,
          changeNotes: newVersionFile.changeNotes,
        },
        newVersionFile.file,
        accessToken || undefined
      );

      // Update the file in the list
      const updatedFiles = files.map((f) => {
        const fId = f.id || f._id || '';
        if (fId === fileId) {
          return updatedFile;
        }
        return f;
      });

      onFilesChange(updatedFiles);
      setNewVersionFile(null);
    } catch (error) {
      console.error('Error updating file:', error);
    } finally {
      setUpdatingFileId(null);
    }
  };

  const handleDeleteFile = async (file: ProjectFile) => {
    const fileId = file.id || file._id || '';
    if (!confirm(`Are you sure you want to delete "${file.originalFilename}"?`) || !accessToken) return;

    setDeletingFileId(fileId);
    try {
      await api.projectFiles.deleteFile(projectId, fileId, undefined, accessToken);

      const updatedFiles = files.filter((f) => {
        const fId = f.id || f._id || '';
        return fId !== fileId;
      });

      onFilesChange(updatedFiles);
    } catch (error) {
      console.error('Error deleting file:', error);
    } finally {
      setDeletingFileId(null);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Project Files</h3>
      {files.length === 0 ? (
        <p className="text-[var(--color-subtext-color)] text-sm">No files uploaded yet.</p>
      ) : (
        <div className="space-y-4">
          {files.map((file) => {
            const fileId = file.id || file._id || '';
            const isEditing = editingFileId === fileId;
            const isUpdating = updatingFileId === fileId;
            const isDeleting = deletingFileId === fileId;
            const metadata = fileMetadata[fileId] || { description: '', synopsis: '' };

            return (
              <div key={fileId} className="border border-[var(--color-neutral-border)] rounded-lg p-4 bg-[var(--color-default-background)]">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3 flex-1">
                    {file.type === 'image' && file.thumbnailUrl ? (
                      <img
                        src={file.thumbnailUrl}
                        alt={file.originalFilename}
                        className="w-16 h-16 object-cover rounded"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-[var(--color-neutral-100)] rounded flex items-center justify-center">
                        <svg className="w-8 h-8 text-[var(--color-neutral-400)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-[var(--color-default-font)] truncate">{file.originalFilename}</h4>
                      <div className="text-sm text-[var(--color-subtext-color)] space-y-1 mt-1">
                        <div>Type: {file.type} • Size: {formatFileSize(file.size)}</div>
                        <div>Added: {formatDate(file.uploadedAt)}</div>
                        {file.updatedAt && (
                          <div>Updated: {formatDate(file.updatedAt)} (v{file.currentVersion || 1})</div>
                        )}
                        {file.versions && file.versions.length > 1 && (
                          <div className="text-xs text-[var(--color-brand-primary)]">
                            {file.versions.length} version{file.versions.length > 1 ? 's' : ''} available
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {!isEditing && (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleEdit(file);
                          }}
                          disabled={isUpdating || isDeleting}
                        >
                          Edit
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setNewVersionFile({ fileId, file: null, changeNotes: '' });
                          }}
                          disabled={isUpdating || isDeleting}
                        >
                          Update
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteFile(file);
                          }}
                          disabled={isUpdating || isDeleting}
                        >
                          {isDeleting ? 'Deleting...' : 'Delete'}
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {isEditing && (
                  <div className="mt-4 space-y-3 border-t pt-3">
                    <Input
                      label="Description"
                      value={metadata.description}
                      onChange={(e) =>
                        setFileMetadata({
                          ...fileMetadata,
                          [fileId]: { ...metadata, description: e.target.value },
                        })
                      }
                      placeholder="Brief description of the file"
                    />
                    {file.type === 'document' && (
                      <div>
                        <label className="block text-sm font-medium text-[var(--color-default-font)] mb-1">
                          Synopsis
                        </label>
                        <textarea
                          value={metadata.synopsis}
                          onChange={(e) =>
                            setFileMetadata({
                              ...fileMetadata,
                              [fileId]: { ...metadata, synopsis: e.target.value },
                            })
                          }
                          placeholder="Document synopsis or summary"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          rows={3}
                        />
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Button type="button" size="sm" onClick={() => handleSaveMetadata(file)}>
                        Save
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={handleCancelEdit}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {newVersionFile && newVersionFile.fileId === fileId && (
                  <div className="mt-4 space-y-3 border-t pt-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Upload New Version
                      </label>
                      <input
                        type="file"
                        onChange={(e) => {
                          const selectedFile = e.target.files?.[0];
                          if (selectedFile) {
                            setNewVersionFile({ ...newVersionFile, file: selectedFile });
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <Input
                      label="Change Notes"
                      value={newVersionFile.changeNotes}
                      onChange={(e) =>
                        setNewVersionFile({ ...newVersionFile, changeNotes: e.target.value })
                      }
                      placeholder="Describe what changed in this version"
                    />
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => handleUpdateFile(file)}
                        disabled={!newVersionFile.file || isUpdating}
                        isLoading={isUpdating}
                      >
                        Upload Version
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setNewVersionFile(null)}
                        disabled={isUpdating}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {file.description && (
                  <div className="mt-3 text-sm text-gray-600">
                    <strong>Description:</strong> {file.description}
                  </div>
                )}

                {file.type === 'document' && file.synopsis && (
                  <div className="mt-2 text-sm text-gray-600">
                    <strong>Synopsis:</strong> {file.synopsis}
                  </div>
                )}

                {file.versions && file.versions.length > 1 && (
                  <div className="mt-3 border-t pt-3">
                    <details className="text-sm">
                      <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
                        View Version History ({file.versions.length} versions)
                      </summary>
                      <div className="mt-2 space-y-2">
                        {file.versions
                          .sort((a, b) => b.version - a.version)
                          .map((version) => (
                            <div key={version.version} className="pl-4 border-l-2 border-gray-200">
                              <div className="font-medium">Version {version.version}</div>
                              <div className="text-xs text-gray-500">
                                {formatDate(version.uploadedAt)} by {version.uploadedBy}
                              </div>
                              {version.changeNotes && (
                                <div className="text-xs text-gray-600 mt-1">
                                  {version.changeNotes}
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    </details>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProjectFilesManager;

